import type { SemanticTokens, OpacityScale } from '@/theme/types'

export const injectSemanticCSSVariables = (semantic: SemanticTokens, opacity: OpacityScale): void => {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  const setVar = (name: string, value: string | number) => {
    root.style.setProperty(name, String(value))
  }

  // New semantic tokens
  setVar('--color-primary', semantic.colors.primary)
  setVar('--color-primary-hover', semantic.colors.primaryHover)
  setVar('--color-primary-active', semantic.colors.primaryActive)
  setVar('--color-background', semantic.colors.background)
  setVar('--color-surface', semantic.surface.surface)
  setVar('--color-surface-elevated', semantic.surface.surfaceElevated)
  setVar('--color-border', semantic.colors.border)
  setVar('--color-text-primary', semantic.colors.textPrimary)
  setVar('--color-text-secondary', semantic.colors.textSecondary)
  setVar('--color-text-muted', semantic.colors.textMuted)
  setVar('--color-text-on-primary', semantic.colors.textOnPrimary)
  setVar('--color-text-on-surface', semantic.colors.textOnSurface)
  setVar('--color-text-on-elevated-surface', semantic.colors.textOnElevatedSurface)
  setVar('--color-text-on-light', semantic.colors.textOnLight)
  setVar('--color-text-on-dark', semantic.colors.textOnDark)
  setVar('--color-icon-primary', semantic.colors.iconPrimary)
  setVar('--color-icon-muted', semantic.colors.iconMuted)
  setVar('--color-error', semantic.colors.error)
  setVar('--color-warning', semantic.colors.warning)
  setVar('--color-success', semantic.colors.success)
  setVar('--color-info', semantic.colors.info)

  // Gradients using semantic tokens
  setVar('--gradient-primary', `linear-gradient(135deg, ${semantic.colors.primary} 0%, ${semantic.colors.primaryHover} 100%)`)

  // Interaction tokens
  setVar('--interaction-hover', semantic.interaction.hover)
  setVar('--interaction-active', semantic.interaction.active)
  setVar('--interaction-focus', semantic.interaction.focus)
  setVar('--interaction-disabled', semantic.interaction.disabled)
  setVar('--focus-ring-color', semantic.interaction.focusRing)
  setVar('--outline-color', semantic.interaction.outline)
  setVar('--selection-color', semantic.interaction.selection)

  // Opacity tokens
  setVar('--opacity-disabled', opacity.disabled)
  setVar('--opacity-hover', opacity.hover)
  setVar('--opacity-focus', opacity.focus)
  setVar('--opacity-overlay', opacity.overlay)
  setVar('--opacity-loading', opacity.loading)

  // Primary color with opacity variants
  setVar('--color-primary-opacity-10', `rgba(${hexToRgb(semantic.colors.primary)}, 0.1)`)
  setVar('--color-primary-opacity-20', `rgba(${hexToRgb(semantic.colors.primary)}, 0.2)`)
  setVar('--color-primary-opacity-30', `rgba(${hexToRgb(semantic.colors.primary)}, 0.3)`)

  // Compatibility aliases for shared CSS variables.
  setVar('--primary-color', semantic.colors.primary)
  setVar('--text-dark', semantic.colors.textPrimary)
  setVar('--text-light', semantic.colors.textPrimary)
  setVar('--text-neutral', semantic.colors.textSecondary)
  setVar('--bg-light', semantic.colors.background)
  setVar('--bg-dark', semantic.colors.background)
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '0, 0, 0'
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

